import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import type { SeoFaqItem } from "../seo-content";

export function FaqSection({
  eyebrow,
  title,
  intro,
  items,
  accent,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  items: SeoFaqItem[];
  accent: string;
}) {
  return (
    <section className="mt-12 border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
      <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: accent }}>
        {eyebrow}
      </p>
      <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">{title}</h2>
      <p className="mt-3 max-w-3xl text-sm font-bold leading-6 text-zinc-700 sm:text-base">{intro}</p>

      <div className="mt-6 grid gap-4">
        {items.map((item) => (
          <article key={item.question} className="border-4 border-black bg-[#F8F6EF] p-5">
            <h3 className="text-base font-black text-black sm:text-lg">{item.question}</h3>
            <p className="mt-3 text-sm font-bold leading-6 text-zinc-700">{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function LinkClusterSection({
  eyebrow,
  title,
  intro,
  accent,
  links,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  accent: string;
  links: Array<{ href: string; title: string; summary: string; meta?: ReactNode }>;
}) {
  return (
    <section className="mt-12 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
      <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: accent }}>
        {eyebrow}
      </p>
      <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">{title}</h2>
      <p className="mt-3 max-w-3xl text-sm font-bold leading-6 text-zinc-300 sm:text-base">{intro}</p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="group border-4 border-white/20 bg-white/10 p-5 transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-black uppercase text-white">{link.title}</h3>
                <p className="mt-3 text-sm font-bold leading-6 text-zinc-200">{link.summary}</p>
                {link.meta ? <div className="mt-4 text-[11px] font-black uppercase tracking-[0.18em] text-zinc-400">{link.meta}</div> : null}
              </div>
              <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-white transition-transform group-hover:translate-x-1" strokeWidth={3} />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
